import { Text, cn } from "@musa.tech/design-system";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDashboardFilterOptions } from "@/hooks/useDashboardFilterOptions";
import {
  FILTER_LABELS,
  type DashboardFilterKey,
  type DashboardFilters,
} from "@/hooks/useDashboardFilters";
import { AppliedFilterChips } from "./AppliedFilterChips";
import { DateRangePicker } from "./DateRangePicker";
import { OptionsFilterDropdown } from "./OptionsFilterDropdown";

/** Nome do campo no evento de filtro (GR-712, RN-01 `pagina_acao`) — mantido
 *  em português para não recadastrar o evento no GA4. */
const ANALYTICS_FIELD_BY_KEY: Record<DashboardFilterKey, string> = {
  period: "periodo",
  materials: "residuo",
  states: "estado",
  establishments: "estabelecimento",
  generators: "gerador",
};

interface DashboardFilterBarProps {
  /** Título da página, exibido à esquerda na faixa. */
  title: string;
  filters: DashboardFilters;
  onChange: (next: Partial<DashboardFilters>) => void;
  onRemoveFilter: (key: DashboardFilterKey, value?: string) => void;
  onClearAll: () => void;
  /** Filtros exibidos, na ordem. Gerador entra sozinho quando aplicável. */
  visibleFilters?: DashboardFilterKey[];
  className?: string;
  /** Identifica a tela no registro de uso de filtro (GR-712). */
  analyticsPrefix: string;
}

const DEFAULT_VISIBLE: DashboardFilterKey[] = [
  "period",
  "materials",
  "states",
  "establishments",
];

/** Rótulos longos: ocupam a linha inteira no mobile para não truncar. */
const FULL_ROW_ON_MOBILE: DashboardFilterKey[] = ["period", "materials"];

/**
 * Barra de filtros das páginas de dashboard (Overview, Performance, Indicador
 * Ambiental). Visual do Figma 124:12956; opções e cascata vêm de
 * `useDashboardFilterOptions`.
 */
export const DashboardFilterBar = ({
  title,
  filters,
  onChange,
  onRemoveFilter,
  onClearAll,
  visibleFilters = DEFAULT_VISIBLE,
  className,
  analyticsPrefix,
}: DashboardFilterBarProps) => {
  const {
    isLoading,
    materialOptions,
    stateOptions,
    establishmentOptions,
    generatorOptions,
    showGeneratorFilter,
    chips,
    withGenerators,
  } = useDashboardFilterOptions(filters);
  const { trackButtonClick } = useAnalytics();

  /** Dispara na escolha efetiva do valor, não na abertura do seletor (GR-712, D-01). */
  const trackFilter = (key: DashboardFilterKey) =>
    trackButtonClick({ buttonLabel: `${analyticsPrefix}_filtrar_${ANALYTICS_FIELD_BY_KEY[key]}` });

  const dropdowns: Record<DashboardFilterKey, JSX.Element | null> = {
    period: (
      // Reaproveita o seletor de período que já existia no portal — mesmo
      // calendário de dois meses das outras telas, no formato das pílulas.
      <DateRangePicker
        value={filters.dateRange}
        onChange={(dateRange) => {
          trackFilter("period");
          onChange({ dateRange });
        }}
        className="h-8 w-auto justify-center gap-2 rounded-lg border-transparent bg-white px-4 font-inter text-sm font-semibold text-neutral-800 hover:bg-neutral-50"
      />
    ),
    materials: (
      <OptionsFilterDropdown
        label={FILTER_LABELS.materials}
        options={materialOptions}
        value={filters.materials}
        onChange={(materials) => {
          trackFilter("materials");
          onChange({ materials });
        }}
        searchPlaceholder="Buscar resíduo..."
        emptyMessage="Nenhum tipo de resíduo disponível"
        isLoading={isLoading}
      />
    ),
    states: (
      <OptionsFilterDropdown
        label={FILTER_LABELS.states}
        options={stateOptions}
        value={filters.states}
        onChange={(states) => {
          trackFilter("states");
          onChange({ states });
        }}
        searchPlaceholder="Buscar estado..."
        emptyMessage="Nenhum estado disponível"
        isLoading={isLoading}
      />
    ),
    establishments: (
      <OptionsFilterDropdown
        label={FILTER_LABELS.establishments}
        options={establishmentOptions}
        value={filters.establishments}
        onChange={(establishments) => {
          trackFilter("establishments");
          onChange({ establishments });
        }}
        searchPlaceholder="Buscar estabelecimento..."
        emptyMessage="Nenhum estabelecimento disponível"
        isLoading={isLoading}
        showSelectAll
      />
    ),
    generators: showGeneratorFilter ? (
      <OptionsFilterDropdown
        label={FILTER_LABELS.generators}
        options={generatorOptions}
        value={filters.generators}
        onChange={(generators) => {
          trackFilter("generators");
          onChange(withGenerators(generators));
        }}
        searchPlaceholder="Buscar gerador..."
        emptyMessage="Nenhum gerador disponível"
        isLoading={isLoading}
      />
    ) : null,
  };

  const visible = showGeneratorFilter
    ? [...visibleFilters, "generators" as DashboardFilterKey]
    : visibleFilters;

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex flex-col items-start gap-3 rounded-lg bg-white px-4 py-3 md:flex-row md:items-start md:gap-6 md:py-2">
        <Text
          variant="heading"
          size="small"
          className="shrink-0 whitespace-nowrap text-lg font-semibold leading-7 tracking-[1px] text-neutral-600"
        >
          {title}
        </Text>

        {/* Mobile: grade de duas colunas com pílulas de largura cheia.
            Desktop: linha única alinhada à direita do título, quebrando em
            várias linhas (md:flex-wrap) quando os filtros não cabem — o
            título nunca encolhe (shrink-0 acima) pra não vazar texto por
            cima dele. */}
        <div className="grid w-full grid-cols-2 items-center gap-2 md:flex md:w-auto md:flex-1 md:flex-wrap md:justify-end md:gap-4">
          <Text
            variant="label"
            size="small"
            className="col-span-2 shrink-0 text-neutral-600 md:col-span-1"
          >
            Filtrar por
          </Text>
          {visible.map((key) => (
            <div
              key={key}
              className={cn(
                "w-full md:w-auto",
                "[&_button]:w-full [&_button]:justify-between [&_button]:px-3 md:[&_button]:w-auto md:[&_button]:justify-center md:[&_button]:px-4",
                FULL_ROW_ON_MOBILE.includes(key) && "col-span-2 md:col-span-1",
              )}
            >
              {dropdowns[key]}
            </div>
          ))}
        </div>
      </div>

      <AppliedFilterChips
        chips={chips}
        onRemove={onRemoveFilter}
        onClearAll={onClearAll}
      />
    </div>
  );
};
